/**
 * Team Carousel Component
 * Smooth horizontal scrolling with drag support and navigation arrows
 */
(function () {
  'use strict';

  class TeamCarousel {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector('[data-team-carousel]');
      this.prevBtn = section.querySelector('[data-carousel-prev]');
      this.nextBtn = section.querySelector('[data-carousel-next]');

      if (!this.carousel) return;

      this.isDown = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.velocity = 0;
      this.lastX = 0;
      this.lastTime = 0;

      this.init();
    }

    init() {
      this.isMobile = 'ontouchstart' in window;

      this.bindDragEvents();
      this.bindArrowEvents();
      this.bindBioToggle();
      this.updateArrowStates();
      this.updateScrollHints();

      // Update arrow states and scroll hints on scroll
      this.carousel.addEventListener('scroll', () => {
        this.updateArrowStates();
        this.updateScrollHints();
      });
    }

    bindDragEvents() {
      // Mouse events
      this.carousel.addEventListener('mousedown', (e) => this.handleDragStart(e));
      this.carousel.addEventListener('mousemove', (e) => this.handleDragMove(e));
      this.carousel.addEventListener('mouseup', () => this.handleDragEnd());
      this.carousel.addEventListener('mouseleave', () => this.handleDragEnd());

      // Touch events
      this.carousel.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: true });
      this.carousel.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: true });
      this.carousel.addEventListener('touchend', () => this.handleDragEnd());

      // Prevent link clicks during drag
      this.carousel.addEventListener('click', (e) => {
        if (this.wasDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    handleDragStart(e) {
      this.isDown = true;
      this.wasDragging = false;
      this.carousel.classList.add('is-dragging');
      this.startX = this.getX(e);
      this.scrollLeft = this.carousel.scrollLeft;
      this.lastX = this.startX;
      this.lastTime = Date.now();
      this.velocity = 0;
    }

    handleDragMove(e) {
      if (!this.isDown) return;

      const x = this.getX(e);
      const deltaX = x - this.startX;

      // Mark as dragging if moved more than threshold
      if (Math.abs(deltaX) > 5) {
        this.wasDragging = true;
      }

      // Calculate velocity for momentum
      const now = Date.now();
      const dt = now - this.lastTime;
      if (dt > 0) {
        this.velocity = (x - this.lastX) / dt;
      }
      this.lastX = x;
      this.lastTime = now;

      this.carousel.scrollLeft = this.scrollLeft - deltaX;
    }

    handleDragEnd() {
      if (!this.isDown) return;
      this.isDown = false;
      this.carousel.classList.remove('is-dragging');

      // Apply momentum scrolling
      if (Math.abs(this.velocity) > 0.1) {
        this.applyMomentum();
      }

      // Reset dragging state after a short delay
      setTimeout(() => {
        this.wasDragging = false;
      }, 10);
    }

    applyMomentum() {
      const deceleration = 0.95;
      const minVelocity = 0.1;

      const animate = () => {
        if (Math.abs(this.velocity) < minVelocity) return;

        this.carousel.scrollLeft -= this.velocity * 16;
        this.velocity *= deceleration;

        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }

    getX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    }

    bindArrowEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollToPrev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollToNext());
      }
    }

    scrollToPrev() {
      const cardWidth = this.getCardWidth();
      this.carousel.scrollBy({
        left: -cardWidth,
        behavior: 'smooth',
      });
    }

    scrollToNext() {
      const cardWidth = this.getCardWidth();
      this.carousel.scrollBy({
        left: cardWidth,
        behavior: 'smooth',
      });
    }

    getCardWidth() {
      const card = this.carousel.querySelector('.about-team__card');
      if (!card) return 300;

      const styles = getComputedStyle(this.carousel);
      const gap = parseFloat(styles.gap) || 0;

      return card.offsetWidth + gap;
    }

    updateArrowStates() {
      if (!this.prevBtn || !this.nextBtn) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.carousel;
      const isAtStart = scrollLeft <= 10;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;

      this.prevBtn.disabled = isAtStart;
      this.nextBtn.disabled = isAtEnd;
    }

    updateScrollHints() {
      const wrapper = this.carousel.closest('.about-team__carousel-wrapper');
      if (!wrapper) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.carousel;
      wrapper.classList.toggle('has-scrolled-start', scrollLeft > 10);
      wrapper.classList.toggle('has-scrolled-end', scrollLeft >= scrollWidth - clientWidth - 10);
    }

    bindBioToggle() {
      if (!this.isMobile) return;

      const cards = this.carousel.querySelectorAll('.about-team__card');
      cards.forEach((card) => {
        const bioOverlay = card.querySelector('.about-team__bio-overlay');
        if (!bioOverlay) return;

        card.addEventListener('click', (e) => {
          // Don't toggle bio if user was dragging
          if (this.wasDragging) return;
          // Don't toggle if clicking a link
          if (e.target.closest('a')) return;

          e.preventDefault();
          const isVisible = bioOverlay.classList.contains('is-bio-visible');

          // Close all other bios
          this.carousel.querySelectorAll('.about-team__bio-overlay.is-bio-visible').forEach((overlay) => {
            overlay.classList.remove('is-bio-visible');
          });

          if (!isVisible) {
            bioOverlay.classList.add('is-bio-visible');
          }
        });
      });

      // Close bios when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.about-team__card')) {
          this.carousel.querySelectorAll('.about-team__bio-overlay.is-bio-visible').forEach((overlay) => {
            overlay.classList.remove('is-bio-visible');
          });
        }
      });
    }
  }

  // Initialize all team carousels
  function initTeamCarousels() {
    const sections = document.querySelectorAll('.about-team-section');
    sections.forEach((section) => {
      if (!section.dataset.initialized) {
        new TeamCarousel(section);
        section.dataset.initialized = 'true';
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTeamCarousels);
  } else {
    initTeamCarousels();
  }

  // Re-initialize for Shopify theme editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      const section = event.target;
      if (section.classList.contains('about-team-section')) {
        section.dataset.initialized = '';
        new TeamCarousel(section);
        section.dataset.initialized = 'true';
      }
    });
  }
})();
